import React, { useEffect, useState } from 'react';
import SubstituteSessions from '../components/SubstituteSessions';   // 代講する授業【2026-11-04】
import { useNavigate, useSearchParams } from 'react-router-dom';
import YearChangePanel from '../components/YearChangePanel';
import ManabaRosterImport from '../components/ManabaRosterImport';
import KyomuRosterImport from '../components/KyomuRosterImport';
import { usePageFileDrop, PageDropOverlay } from '../components/PageFileDrop';
import { STATUS } from '../components/StudentStatusBadges';
import { usePeriod } from '../components/PeriodBar';
import Notice from '../components/Notice';
import { confirmDialog, promptDialog } from '../components/ConfirmDialog';

const DAY_NAMES = ['日','月','火','水','木','金','土'];

export default function ClassManagement() {
  const navigate = useNavigate();
  // サイドバーのサブメニューから「授業未選択」で来たとき、
  // どのページを開きたかったかを引き継ぐ【2026-09-07】
  const [searchParams] = useSearchParams();
  // 受講者名簿は学生管理へ移したので、既定は出席記録【2026-09-17】
  const wantSection = searchParams.get('section') || 'summary';
  const SECTION_LABEL = {
    schedule: '授業スケジュール', roster: '受講者名簿', summary: '出席記録',
  };
  const [classes, setClasses]   = useState([]);
  const [err, setErr]           = useState('');
  const [msg, setMsg]           = useState('');

  // 新規作成フォーム
  const [newName, setNewName]       = useState('');
  const [newSemester, setNewSemester] = useState('');
  const [newDesc, setNewDesc]       = useState('');

  // 一覧の絞り込みと追加モーダル【2026-09-04 画面刷新】
  const [query, setQuery]           = useState('');
  // 年度・学期【2026-09-14】
  //   選択肢は大学ごとのマスタ（/api/terms）から引く。二期制・三期制の違いは
  //   サーバー側のデータの違いなので、画面に '前期'/'後期' を書かないこと。
  const [terms, setTerms]           = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [newYear, setNewYear]       = useState('');   // 新規作成・名簿取り込みに使う年度
  const [newTerm, setNewTerm]       = useState('');
  // 一覧は「今の期間」（教務が決めた学期の期間から自動）の授業だけ出す【2026-09-17】
  //   【2026-09-28】期間は「授業を追加」の年度・学期のプルダウンで選ぶ（一覧と連動）。
  const period = usePeriod();
  const [yearFilter, setYearFilter] = useState('');   // '' = 期間で表示 / 'hold' = 年度保留中
  const [showArchived, setShowArchived] = useState(false);
  const [heldCount, setHeldCount]   = useState(0);   // 年度保留中の授業の数【2026-09-17】
  const [yearEdit, setYearEdit]     = useState(null); // 年度を直す授業
  const [addOpen, setAddOpen]       = useState(false);
  const [addTab, setAddTab]         = useState('manual'); // 'manual' | 'manaba'

  // 画面のどこに名簿ファイルを落としても取り込めるようにする【2026-09-17】
  //   追加モーダルの「manabaから一括」を開いていればそちら、そうでなければ上の「授業を追加」へ渡す
  //
  //   【2026-09-18】名簿の入口は下のドロップ枠1つ。
  //   manaba か教務システムの受講者一覧かは拡張子では分からないので、
  //   そこがサーバーに見出しを見せて振り分ける。ここは渡すだけ。
  const [incomingFile, setIncomingFile] = useState(null);
  const [incomingKyomu, setIncomingKyomu] = useState(null);
  const pageDragging = usePageFileDrop((files) => {
    const f = files[0];
    if (!/\.(csv|tsv|xls|xlsx|xlsm|ods)$/i.test(f.name)) {
      setErr(f.name + '： 名簿は Excel（.xls / .xlsx）・.ods・CSV のどれかを落としてください');
      return;
    }
    setErr('');
    setIncomingFile(f);
  }, { enabled: !yearEdit });

  const token = () => localStorage.getItem('token');
  const auth  = () => ({ Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' });

  const loadClasses = async () => {
    try {
      // 【2026-09-04】/classes は class_schedules を LEFT JOIN していて
      //   週2コマの授業が2行に増えるため、一覧専用の overview を使う。
      // 既定は「今の年度・アーカイブ以外」。授業は年々溜まるので、
      //   指定しなければ今年のものだけが返る（絞り込みはサーバー側）。
      const qs = new URLSearchParams();
      if (yearFilter === 'hold') qs.append('academicYear', 'hold');
      else if (period.year) qs.append('academicYear', String(period.year));
      if (showArchived) qs.append('includeArchived', '1');
      const res  = await fetch('/api/schedule/classes/overview?' + qs, { headers: auth() });
      const data = await res.json();
      setClasses(data.classes || data.data || []);
      setHeldCount(data.heldCount || 0);
    } catch (e) { setErr('授業取得失敗: ' + e.message); }
  };

  // 学期マスタと既定値（今日の年度・学期）
  const loadTerms = async () => {
    try {
      const res  = await fetch('/api/terms', { headers: auth() });
      const data = await res.json();
      if (!data.success) return;
      setTerms(data.terms || []);
      setYearOptions(data.yearOptions || []);
      if (data.defaults) {
        // 一覧で「ほかの期間」を選んでいれば、そちらを優先する（下の同期で入る）【2026-09-28】
        setNewYear(v => v || String(data.defaults.academicYear ?? ''));
        setNewTerm(v => v || (data.defaults.termId ? String(data.defaults.termId) : ''));
      }
    } catch (e) { /* 学期が引けなくても既定値でサーバー側が補う */ }
  };

  useEffect(() => { loadTerms(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 画面を開いたときに1回だけ

  // 「授業を追加」の年度・学期と、一覧の期間をつなぐ【2026-09-28】
  //   追加欄で年度・学期を選ぶと、一覧もその年度・学期の授業になる。
  //   一覧の「ほかの期間」で選んだときは、追加欄の年度・学期もそれに合わせる。
  useEffect(() => {
    if (!period.ready || period.year == null) return;
    setNewYear(String(period.year));
    if (period.termId != null && period.termId !== 'all') setNewTerm(String(period.termId));
  }, [period.ready, period.year, period.termId]);
  const pickYear = (v) => {
    setNewYear(v);
    if (v) period.select(Number(v), newTerm ? Number(newTerm) : 'all');
  };
  const pickTerm = (v) => {
    setNewTerm(v);
    if (newYear && v) period.select(Number(newYear), Number(v));
  };            // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (period.ready) loadClasses(); }, [yearFilter, showArchived, period.ready, period.year]); // eslint-disable-line react-hooks/exhaustive-deps

  const createClass = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    // 学期の変わり目は、既定の学期が思っているものと違いやすい【2026-09-18】
    //   前期の終わりや後期の始まりは年によって数日ずれるので、
    //   境目の前後1週間だけ、どの学期で作るかを一度確かめる。
    if (!(await confirmNearBoundary())) return;
    try {
      const res = await fetch('/api/schedule/classes', {
        method: 'POST', headers: auth(),
        body: JSON.stringify({
          name: newName, semester: newSemester, description: newDesc,
          academicYear: newYear, termId: newTerm,
        })
      });
      const d = await res.json();
      if (!d.success && !d.class && !d.id) throw new Error(d.message || '作成失敗');
      setMsg('授業を作成しました'); setNewName(''); setNewSemester(''); setNewDesc('');
      setAddOpen(false);
      loadClasses();
    } catch (e) { setErr(e.message); }
  };

  /**
   * 学期の境目から1週間以内なら、どの学期で作るかを確かめる【2026-09-18】
   *   期間を上書きしていない年度は毎年の月日が使われるので、
   *   実際の学期暦と数日ずれることがある。
   * @returns {boolean} 進めてよければ true
   */
  const confirmNearBoundary = async () => {
    const s2 = period.startDate, e2 = period.endDate;
    if (!period.isCurrent || !s2 || !e2) return true;
    const DAY = 24 * 60 * 60 * 1000;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const near = (iso) => {
      const d = new Date(iso + 'T00:00:00');
      return Math.abs((d - today) / DAY) <= 7;
    };
    if (!near(s2) && !near(e2)) return true;
    return (await confirmDialog(
      `いまは学期の変わり目です（${period.label}：${s2}〜${e2}）。\n\n` +
      `「${newName.trim()}」を ${period.label} の授業として作ります。\n` +
      'これでよければOK、違う学期にしたいときはキャンセルして、' +
      '上の「ほかの期間」で学期を選び直してください。'
    ));
  };

  // 一覧から外す（記録は消さない）。年度の締めで使っている archived と同じ印。
  const archiveClass = async (cls, on) => {
    try {
      const res = await fetch('/api/schedule/classes/' + cls.id, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: cls.name, semester: cls.semester, description: cls.description,
          archived: on,
        }),
      });
      const d = await res.json();
      if (!res.ok && !d.success) throw new Error(d.message || '変更に失敗しました');
      setErr('');
      setMsg(on
        ? '一覧から畳みました。完全に削除するには「畳んだ授業も表示」から'
        : '一覧に戻しました');
      loadClasses();
    } catch (e) { setErr(e.message); }
  };

  // 教室を直す【2026-09-28】名簿（教務システム）から入るが、変わったときは手で直せる
  const editRoom = async (cls) => {
    const v = (await promptDialog(`「${cls.name}」の教室`, cls.room || ''));
    if (v === null) return;
    try {
      const res = await fetch('/api/schedule/classes/' + cls.id, {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({ name: cls.name, semester: cls.semester, description: cls.description, room: v }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || '教室を保存できませんでした');
      setErr(''); setMsg(v.trim() ? `教室を「${v.trim()}」にしました` : '教室を消しました');
      loadClasses();
    } catch (e) { setErr(e.message); }
  };

  // 削除は2段階。畳んだ授業だけ、出席記録ごと完全に消せる。
  //   1回目は件数を聞くだけ（サーバーが 409 + count を返す）。
  //   画面でその件数を見せて、了承が取れたら同じ件数を送り返して実行する。
  const deleteClass = async (cls) => {
    const send = (confirmRecords) => fetch('/api/schedule/classes/' + cls.id, {
      method: 'DELETE',
      headers: confirmRecords === undefined
        ? auth()
        : { ...auth(), 'Content-Type': 'application/json' },
      ...(confirmRecords === undefined ? {} : { body: JSON.stringify({ confirmRecords }) }),
    });

    try {
      const res = await send();
      const d   = await res.json();

      if (d.code === 'CONFIRM_REQUIRED') {
        const warn = d.count > 0
          ? `「${cls.name}」を完全に削除します。\n\n出席記録 ${d.count} 件も一緒に消えます。\n元には戻せません。\n\n本当に削除しますか？`
          : `「${cls.name}」を完全に削除します。\n元には戻せません。\n\n本当に削除しますか？`;
        if (!(await confirmDialog(warn))) return;

        const res2 = await send(d.count);
        const d2   = await res2.json();
        if (!res2.ok && !d2.success) throw new Error(d2.message || '削除に失敗しました');
        forgetLastClass(cls.id);
        setErr(''); setMsg(d2.message || '削除しました');
        loadClasses();
        return;
      }

      if (!res.ok && !d.success) throw new Error(d.message || '削除に失敗しました');

      forgetLastClass(cls.id);
      setErr(''); setMsg(d.message || '削除しました');
      loadClasses();
    } catch (e) { setErr(e.message); }
  };

  // 消した授業をサイドバーの行き先に使い続けないようにする
  const forgetLastClass = (id) => {
    try {
      if (localStorage.getItem('lastClassId') === String(id)) {
        localStorage.removeItem('lastClassId');
      }
    } catch (e) { /* 参照できなくても動く */ }
  };

  // 絞り込み（授業が増えても探せるように）
  // 【2026-09-28】学期の設定がずれていても、選んでいる期間に出席記録がある授業は出す
  //   （例: 学期の変わり目に既定の「前期」で作った後期の授業）
  const inPeriod = yearFilter === 'hold'
    ? classes
    : classes.filter(c => period.matches(c) || period.hasRecordsIn(c));
  const visible = inPeriod.filter(c => {
    // 検索欄が空なら「授業を追加」の授業名で絞る。どちらも空なら全部出す【2026-09-28】
    //   追加する前に、同じ名前の授業がもうないかを一覧で見られるように。
    const q = (query.trim() || newName.trim()).toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q)
        || (c.semester || '').toLowerCase().includes(q)
        || (c.teacher_name || '').toLowerCase().includes(q);
  });

  const fmtSchedules = (list) => {
    if (!list || list.length === 0) return null;
    return list.map(s => `${DAY_NAMES[s.day_of_week]} ${s.start_time}-${s.end_time}${s.room ? " " + s.room : ""}`);   // 曜日時限ごとの教室も【2026-09-28】
  };

  return (
    <div className="page-container" style={{ paddingBottom: '5rem' }}>
      <PageDropOverlay show={pageDragging} title="ファイルをドロップして名簿を読み込む"
                       note="manaba の履修者名簿として読み込みます（中身を確認してから保存します）" />
      <div className="page-header">
        <h1>授業管理</h1>
        <p>担当授業の一覧です。カードを開くと曜日時限と履修者を編集できます。</p>
      </div>

      {searchParams.get('section') && (
        <div className="card" style={{ borderLeft: '4px solid #2563eb' }}>
          <p style={{ margin: 0 }}>
            授業を選ぶと「{SECTION_LABEL[wantSection] || wantSection}」を開きます。
          </p>
        </div>
      )}

      <Notice kind="error">{err}</Notice>
      <Notice kind="success" toast>{msg}</Notice>

      {/* ほかの先生の授業で代講する回（無ければ出ない）【2026-11-04】 */}
      <SubstituteSessions/>

      {/* 授業を追加（一覧とは別の箱にして目立たせる）【2026-09-07】
          手で1つ作る道と、名簿から一気に作る道を並べて置く。 */}
      <div className="card" style={{ borderTop:'4px solid #2563eb' }}>
        <div className="card-header"><h2 style={{ margin:0 }}>授業を追加</h2></div>

        {/* 手で1つ作る */}
        <form onSubmit={createClass}
              style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center' }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="授業名（例: 情報基礎）"
            style={{ flex:'1 1 260px', padding:'0.55rem 0.7rem', fontSize:'1rem' }}
          />
          <select value={newYear} onChange={e => pickYear(e.target.value)}
                  style={{ flex:'0 1 120px', padding:'0.55rem 0.4rem' }}>
            {yearOptions.length === 0 && <option value="">年度</option>}
            {yearOptions.map(y => <option key={y} value={y}>{y}年度</option>)}
          </select>
          <select value={newTerm} onChange={e => pickTerm(e.target.value)}
                  style={{ flex:'0 1 120px', padding:'0.55rem 0.4rem' }}>
            {terms.length === 0 && <option value="">学期</option>}
            {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn-primary" type="submit" disabled={!newName.trim()}
                  style={{ padding:'0.55rem 1.2rem' }}>
            ＋ 追加
          </button>
        </form>
        <p style={{ fontSize:'0.78rem', color:'#888', margin:'0.4rem 0 0' }}>
          作成後、カードの「詳細」から曜日時限を設定できます。説明も入れたいときは右下の「＋」から。
        </p>

        {/* 区切り */}
        <div style={{ display:'flex', alignItems:'center', gap:'0.7rem', margin:'1rem 0 0.9rem' }}>
          <span style={{ flex:1, height:1, background:'#e5e7eb' }} />
          <span style={{ fontSize:'0.8rem', color:'#9ca3af' }}>または 名簿から一気に作る</span>
          <span style={{ flex:1, height:1, background:'#e5e7eb' }} />
        </div>

        {/* manaba から一括【2026-09-17 確認つきの取り込みに統一】
            中身を確認 → コースごとに行き先（既存／新規／取り込まない）→ 取り込む。
            画面のどこにファイルを落としてもここに入る。 */}
        <ManabaRosterImport
          classes={classes}
          token={token}
          setErr={setErr}
          setMsg={setMsg}
          onImported={() => loadClasses()}
          defaultYear={newYear}
          incomingFile={addOpen && addTab === 'manaba' ? null : incomingFile}
          onIncomingTaken={() => setIncomingFile(null)}
          onKyomuDetected={(f) => setIncomingKyomu(f)}
        />

        {/* 教務システムの受講者一覧だったときだけ出る【2026-09-18】
            名簿の入口は上のドロップ枠1つ。中身を見て、受講者一覧なら
            こちらの確認画面が下に出る。manaba なら上のまま進む。 */}
        <KyomuRosterImport embedded
                           onDone={() => loadClasses()}
                           incomingFile={incomingKyomu}
                           onIncomingTaken={() => setIncomingKyomu(null)}
                           onNotKyomu={(f) => { setIncomingKyomu(null); setIncomingFile(f); }} />
      </div>

      {/* 一覧 */}
      <div className="card">
        <div className="card-header" style={{ display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap' }}>
          <h2 style={{ margin:0 }}>授業一覧（{visible.length}{visible.length !== inPeriod.length && ` / ${inPeriod.length}`}）</h2>
          <div style={{ marginLeft:'auto', display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={newName.trim() ? `「${newName.trim()}」で絞り込み中` : '授業名で検索'}
              style={{ padding:'0.35rem 0.6rem', minWidth:180 }}
            />
            <button className="btn-secondary"
                    onClick={() => navigate('/classes/all/summary')}
                    title="担当している授業をまとめて集計します">
              すべての授業の出席記録
            </button>
            {(heldCount > 0 || yearFilter === 'hold') && (
              <button className={yearFilter === 'hold' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => setYearFilter(f => (f === 'hold' ? '' : 'hold'))}
                      title="年度を確かめている途中の授業">
                年度保留中（{heldCount}）
              </button>
            )}
            <label style={{ fontSize:'0.82rem', color:'#666', display:'flex', alignItems:'center', gap:'0.3rem' }}>
              <input type="checkbox" checked={showArchived}
                     onChange={e => setShowArchived(e.target.checked)} />
              畳んだ授業も表示
            </label>
          </div>
        </div>
        {yearFilter === 'hold'
          ? <p style={{ fontSize: '0.85rem', color: '#b45309', margin: '0.3rem 0' }}>年度保留中の授業を表示しています（期間に関係なく出します）</p>
          : // 期間は上の「授業を追加」の年度・学期で選ぶ。「ほかの期間」の切り替えは出さない【2026-09-28】
            period.label && (
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.3rem 0' }}>
                {period.label}
                {period.range && `（${period.range}）`}
                {period.hasFullYear && period.term && !period.term.isFullYear && '＋通年'}
                の授業を表示しています
              </p>
            )}

        {classes.length === 0 ? (
          <p style={{ color:'#888', textAlign:'center', padding:'2rem 0' }}>
            まだ授業がありません。上の「授業を追加」から作ってください。
          </p>
        ) : visible.length === 0 ? (
          <p style={{ color:'#888', textAlign:'center', padding:'2rem 0' }}>
            {inPeriod.length === 0 ? 'この期間の授業はありません（前の学期は上の「ほかの期間」から見られます）' : '条件に合う授業がありません'}
          </p>
        ) : (
          <div style={{
            display:'grid',
            gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))',
            gap:'0.9rem'
          }}>
            {visible.map(c => {
              const sched = fmtSchedules(c.schedules);
              return (
                <div key={c.id}
                  style={{
                    border: c.archived ? '1px dashed #cbd5e1' : '1px solid #e5e7eb',
                    borderRadius: 10,
                    padding: '0.9rem 1rem',
                    background: c.archived ? '#f8fafc' : '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:'1.02rem', lineHeight:1.4,
                                  display:'flex', alignItems:'center', gap:'0.4rem', flexWrap:'wrap' }}>
                      {c.name}
                      {c.archived && (
                        <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b',
                                       background:'#e2e8f0', borderRadius:5, padding:'0.05rem 0.4rem' }}>
                          畳んだ授業
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:'0.8rem', color:'#888' }}>
                      {c.semester || '学期未設定'}
                      {c.teacher_name && <> ・ {c.teacher_name}</>}
                      {c.linked_to_class_id && (
                        <span title="別学部の同じ授業として、ほかの授業にまとめています。履修者・出席はまとめ先にあります"
                              style={{ marginLeft:'0.35rem', fontSize:'0.72rem', fontWeight:700, color:'#1d4ed8',
                                       background:'#dbeafe', padding:'0.05rem 0.35rem', borderRadius:4 }}>
                          →「{c.linked_to_name}」にまとめ済み
                        </span>
                      )}
                      {c.linked_children > 0 && (
                        <span title="別学部の同じ授業をこの授業にまとめています"
                              style={{ marginLeft:'0.35rem', fontSize:'0.72rem', fontWeight:700, color:'#1d4ed8',
                                       background:'#dbeafe', padding:'0.05rem 0.35rem', borderRadius:4 }}>
                          {c.linked_children}授業をまとめ中
                        </span>
                      )}
                      {c.year_on_hold && (
                        <span style={{ marginLeft:'0.35rem', fontSize:'0.72rem', fontWeight:700, color:'#92400e',
                                       background:'#fef3c7', padding:'0.05rem 0.35rem', borderRadius:4 }}>
                          年度保留中
                        </span>
                      )}
                      <button type="button"
                              onClick={() => setYearEdit(c)}
                              title="この授業の年度を直す・保留にする"
                              style={{ marginLeft:'0.4rem', fontSize:'0.72rem', padding:'0 0.35rem',
                                       background:'none', border:'1px solid #d1d5db', borderRadius:4,
                                       color:'#555', cursor:'pointer' }}>
                        年度
                      </button>
                    </div>
                  </div>

                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem' }}>
                    {sched
                      ? sched.map((t, i) => (
                          <span key={i} style={{
                            fontSize:'0.75rem', background:'#eef2ff', color:'#3730a3',
                            borderRadius: 999, padding:'0.1rem 0.55rem'
                          }}>{t}</span>
                        ))
                      : <span style={{ fontSize:'0.75rem', color:'#c2410c' }}>曜日時限が未設定</span>}
                    {/* 教室（名簿から入る。クリックで直せる）【2026-09-28】 */}
                    <button type="button" onClick={() => editRoom(c)}
                            title={c.room ? '教室を直す' : '教室を入れる'}
                            style={{ fontSize:'0.75rem', borderRadius:999, padding:'0.1rem 0.55rem', cursor:'pointer',
                                     border: c.room ? '1px solid #d1fae5' : '1px dashed #d1d5db',
                                     background: c.room ? '#ecfdf5' : '#fff', color: c.room ? '#065f46' : '#9ca3af' }}>
                      {c.room ? '教室 ' + c.room : '教室を入れる'}
                    </button>
                  </div>

                  <div style={{ fontSize:'0.82rem', color:'#555' }}>
                    履修 <strong>{c.enrolled_count}</strong>人
                    {c.placeholder_count > 0 && (
                      <span title={STATUS.noAccount.title} style={{ color: STATUS.noAccount.color }}>
                        （うち{STATUS.noAccount.label} {c.placeholder_count}）
                      </span>
                    )}
                    　実施 <strong>{c.held_sessions}</strong>回
                  </div>

                  <div style={{ marginTop:'auto', display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                    <button className="btn-secondary"
                            style={{ flex: c.archived ? '1 1 100%' : 1, whiteSpace:'nowrap' }}
                            onClick={() => navigate('/classes/' + c.id + '/' + wantSection)}>
                      {SECTION_LABEL[wantSection] ? SECTION_LABEL[wantSection] + 'を開く' : '詳細を開く'}
                    </button>
                    {/* 授業スケジュール（曜日時限・教室）へ直接【2026-09-28】 */}
                    {wantSection !== 'schedule' && (
                      <button className="btn-secondary" style={{ whiteSpace:'nowrap' }}
                              title="曜日時限と教室を設定する"
                              onClick={() => navigate('/classes/' + c.id + '/schedule')}>
                        スケジュール
                      </button>
                    )}
                    {/* 削除は2段階。畳んでいない授業には削除ボタンを出さない【2026-09-18】
                        畳んだ授業は操作が2つ増えるので、1行に3つ詰めずに折り返す */}
                    {c.archived ? (
                      <>
                        <button className="btn-secondary" style={{ flex:1, whiteSpace:'nowrap' }}
                                onClick={() => archiveClass(c, false)}
                                title="一覧に戻す">一覧に戻す</button>
                        <button className="btn-danger" style={{ flex:1, whiteSpace:'nowrap' }}
                                onClick={() => deleteClass(c)}
                                title="出席記録ごと完全に削除する（元に戻せません）">完全に削除</button>
                      </>
                    ) : Number(c.attended_count) === 0 ? (
                      // 出席した学生がいない授業は、畳まずにその場で消せる【2026-09-28】
                      <button className="btn-danger" style={{ whiteSpace:'nowrap' }}
                              onClick={() => deleteClass(c)}
                              title="出席した学生がいないので、そのまま削除できます">削除</button>
                    ) : (
                      <button className="btn-secondary" style={{ whiteSpace:'nowrap' }}
                              onClick={() => archiveClass(c, true)}
                              title="記録を残したまま一覧から外す。削除は畳んだあと「畳んだ授業も表示」から">畳む</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 右下の追加ボタン */}
      <button
        onClick={() => { setAddOpen(true); setAddTab('manual'); }}
        title="授業を追加"
        aria-label="授業を追加"
        style={{
          position:'fixed', right:'1.5rem', bottom:'1.5rem',
          width:56, height:56, borderRadius:'50%',
          background:'#2563eb', color:'#fff', border:'none',
          fontSize:'1.8rem', lineHeight:1, cursor:'pointer',
          boxShadow:'0 4px 14px rgba(0,0,0,0.25)', zIndex:50
        }}>
        ＋
      </button>

      {/* 追加モーダル */}
      {addOpen && (
        <div
          onClick={() => setAddOpen(false)}
          style={{
            position:'fixed', inset:0, background:'rgba(0,0,0,0.45)',
            display:'flex', alignItems:'center', justifyContent:'center',
            padding:'1rem', zIndex:60
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:'#fff', borderRadius:12, padding:'1.25rem',
              width:'min(560px, 100%)', maxHeight:'85vh', overflowY:'auto'
            }}>
            <div style={{ display:'flex', alignItems:'center', marginBottom:'0.8rem' }}>
              <h2 style={{ margin:0, fontSize:'1.1rem' }}>授業を追加</h2>
              <button onClick={() => setAddOpen(false)}
                      style={{ marginLeft:'auto', border:'none', background:'none', fontSize:'1.3rem', cursor:'pointer' }}>
                ✕
              </button>
            </div>

            <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem' }}>
              <button className={addTab === 'manual' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => setAddTab('manual')}>1つずつ作る</button>
              <button className={addTab === 'manaba' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => setAddTab('manaba')}>manabaから一括</button>
            </div>

            {addTab === 'manual' ? (
              <form onSubmit={createClass}>
                <div className="form-row">
                  <label>授業名 *</label>
                  <input value={newName} onChange={e => setNewName(e.target.value)} required
                         autoFocus placeholder="例: プログラミング基礎"/>
                </div>
                <div className="form-row">
                  <label>学期</label>
                  <input value={newSemester} onChange={e => setNewSemester(e.target.value)} placeholder="例: 2026年度後期"/>
                </div>
                <div className="form-row">
                  <label>説明</label>
                  <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} rows={2}/>
                </div>
                <p style={{ fontSize:'0.82rem', color:'#666' }}>
                  作成後、カードの「詳細」から曜日時限を設定してください。
                </p>
                <button type="submit" className="btn-primary">作成</button>
              </form>
            ) : (
              <div>
                <ManabaRosterImport
                  classes={classes}
                  token={token}
                  setErr={setErr}
                  setMsg={setMsg}
                  onImported={() => loadClasses()}
                  defaultYear={newYear}
                  incomingFile={incomingFile}
                  onIncomingTaken={() => setIncomingFile(null)}
                  onKyomuDetected={(f) => { setAddOpen(false); setIncomingKyomu(f); }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* 授業1つの年度を直す・保留にする【2026-09-17】 */}
      {yearEdit && (
        <YearChangePanel
          modal
          mode="class"
          targetId={yearEdit.id}
          title={`「${yearEdit.name}」の年度`}
          initialYear={yearEdit.academic_year}
          initialTermId={yearEdit.term_id}
          onCancel={() => setYearEdit(null)}
          onDone={(m) => { setYearEdit(null); setMsg(m || ''); loadClasses(); }}
        />
      )}
    </div>
  );
}
